import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { InvitedUserDetails } from '@/api/v4/auth.api';
import { fetchInvitedUserDetails } from '@/api/v4/auth.api';
import { BaseExceptions, isBaseError } from '@/api/v4/base-fetch';
import { QueryKey } from '@/config/query-client';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { notify } from '@/ui/snackbar/notify';

import { useSignUpParams } from './use-sign-up-params';

export const useInvitedUserDetails = ({
  onSuccessHandler,
}: {
  onSuccessHandler: (data?: InvitedUserDetails) => void;
}) => {
  const { t } = useTranslation('auth');
  const navigateTo = useNavigateTo();
  const { invitedUserParams, areParamsFetched, setFetchedParams } =
    useSignUpParams();

  const query = useQuery({
    queryKey: [QueryKey.InvitedUserData, invitedUserParams],
    queryFn: async () =>
      invitedUserParams
        ? await fetchInvitedUserDetails(invitedUserParams)
        : undefined,
    enabled: !areParamsFetched && !!invitedUserParams,
    retry: false,
  });

  const onSuccessHandlerRef = useRef(onSuccessHandler);
  onSuccessHandlerRef.current = onSuccessHandler;
  const hasHandledSuccessRef = useRef(false);

  useEffect(() => {
    if (query.isSuccess && !hasHandledSuccessRef.current) {
      hasHandledSuccessRef.current = true;

      if (query.data?.isAlreadyActive) {
        notify({ message: t`signUpAccountAlreadySetUp` });
        navigateTo.signIn();
        setFetchedParams(true);
        return;
      }

      onSuccessHandlerRef.current(query.data);
      setFetchedParams(true);
    }
  }, [query.isSuccess, query.data, navigateTo, setFetchedParams, t]);

  useEffect(() => {
    const error = query.error;
    if (!error) return;

    if (isBaseError(error)) {
      if (error.response.status === BaseExceptions.NOT_FOUND) {
        navigateTo.signUpInvitationExpired();
        return;
      }
      notify({ message: t`unknownError` });
      return;
    }

    notify({ message: t`unknownError` });
  }, [query.error, navigateTo, t]);

  const isRedirectingToSignIn =
    query.isSuccess && query.data?.isAlreadyActive === true;

  return {
    ...query,
    isLoading: query.isLoading || isRedirectingToSignIn,
  };
};
